import React, { useEffect, useState } from 'react';
import { useStyles } from './index.style';
import { useAppSelector, useAppDispatch, shallowEqual } from 'store/hook';
import { Form, Steps, Radio, Select, Upload, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { MButton, MForm, MInput, MDatePicker, MSelect } from 'ui-component/antdStyle';
import { SET_USER_INFO } from 'store/userReducer';
import Tips from 'ui-component/Tools/Tips';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Email, NumberCode } from 'constants/regexp';
import * as uploadController from 'controller/upload';
import { MModal } from 'ui-component';
import { getKycInfo, submitKyc } from 'controller/auth';
import { COUNTRY_LIST } from 'constants/country';
import MImage from 'ui-component/Image';
import Notify from 'ui-component/Tools/Notify';
import ProcessIcon from 'ui-component/icons/process';
import dayjs from 'dayjs';

interface IHookProps {
  open: boolean;
}

const CMP: React.FC<IHookProps> = ({ open }) => {
  const { styles, cx } = useStyles();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation(['common']);
  const kycV2Status = useAppSelector((state) => state.user.user?.userStatus?.kycV2Status, shallowEqual);
  const [uploadList, setUploadImage] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const validType = Form.useWatch('validType', form);

  const onClose = () => {
    dispatch(
      SET_USER_INFO({
        openKyc: false
      })
    );
  };

  useEffect(() => {
    if (kycV2Status === 'CHECKING' || kycV2Status === 'FAILED') {
      const getData = async () => {
        const res = await getKycInfo();
        if (res.success) {
          const data = res.data;
          if (data?.status === 'CHECKING') {
            setCurrentStep(1);
          } else if (data?.status === 'FAILED') {
            setCurrentStep(0);
            form.setFieldsValue({
              fullName: data.fullName,
              birthDate: data.birthDate ? dayjs(data.birthDate / 1) : null,
              expireDate: data.expireDate ? dayjs(data.expireDate / 1) : null,
              gender: data.gender,
              idCountry: data.idCountry,
              idNumber: data.idNumber,
              idType: data.idType,
              validType: data.validType
            });
            setUploadImage(data.attachments || []);
          } else if (data?.status === 'INIT') {
            setCurrentStep(0);
          }
        }
      };
      getData();
    }
  }, [kycV2Status]);

  const onSubmit = async () => {
    form
      .validateFields()
      .then(async (values) => {
        if (uploadList.length === 0) {
          Notify('error', t('kycTips4', { ns: 'common' }));
          return;
        }
        setLoading(true);
        const res = await submitKyc({
          fullName: values.fullName,
          birthDate: values.birthDate ? values.birthDate.valueOf() : null,
          expireDate: values.expireDate ? values.expireDate.valueOf() : null,
          attachments: uploadList,
          gender: values.gender,
          idCountry: values.idCountry,
          idNumber: values.idNumber,
          idType: values.idType,
          validType: values.validType
        });
        setLoading(false);
        if (res.success) {
          Notify('success', t('kycTips5', { ns: 'common' }));
          setCurrentStep(1);
        }
      })
      .catch(() => {});
  };

  const beforeUpload = async (file: File) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await uploadController.uploadFile(formData);
    setLoading(false);
    if (res.success) {
      const newList = [...uploadList];
      newList.push(res.data.link);
      setUploadImage(newList);
    }
  };

  const props = {
    name: 'file',
    multiple: false,
    customRequest: () => false,
    beforeUpload,
    accept: '.jpeg,.png,.jpg,.svg,.webp'
  };

  const onRemove = (index: number) => {
    const newList = [...uploadList];
    newList.splice(index, 1);
    setUploadImage(newList);
  };

  return (
    <MModal open={open} title={t('kycTips6', { ns: 'common' })} onCancel={onClose} width={800} loading={loading} hiddenFooter>
      <div className={styles.container}>
        <Steps
          current={currentStep}
          items={[
            {
              title: t('kycTips7', { ns: 'common' })
            },
            {
              title: t('kycTips8', { ns: 'common' })
            },
            {
              title: t('kycTips9', { ns: 'common' })
            }
          ]}
        />

        {currentStep === 0 && (
          <>
            <MForm layout="vertical" form={form} className={styles.warp}>
              <Form.Item
                label={t('kycTips10', { ns: 'common' })}
                name="fullName"
                rules={[{ required: true, message: t('kycTips11', { ns: 'common' }) }]}
              >
                <MInput placeholder={t('kycTips11', { ns: 'common' })} />
              </Form.Item>
              <Form.Item
                label={t('kycTips13', { ns: 'common' })}
                name="birthDate"
                rules={[{ required: true, message: t('kycTips12', { ns: 'common' }) }]}
              >
                <MDatePicker placeholder={t('kycTips12', { ns: 'common' })} />
              </Form.Item>
              <Form.Item
                label={t('kycTips14', { ns: 'common' })}
                name="gender"
                rules={[{ required: true, message: t('kycTips12', { ns: 'common' }) }]}
              >
                <Radio.Group>
                  <Radio value="MALE"> {t('kycTips15', { ns: 'common' })} </Radio>
                  <Radio value="FEMALE"> {t('kycTips16', { ns: 'common' })} </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={t('kycTips17', { ns: 'common' })}
                name="idCountry"
                rules={[{ required: true, message: t('kycTips12', { ns: 'common' }) }]}
              >
                <MSelect placeholder={t('kycTips12', { ns: 'common' })}>
                  {COUNTRY_LIST.map((x) => {
                    return (
                      <Select.Option key={x.code} value={x.code}>
                        {i18n.language === 'en' ? x.en : x.zh}
                      </Select.Option>
                    );
                  })}
                </MSelect>
              </Form.Item>
              <Form.Item
                label={t('kycTips18', { ns: 'common' })}
                name="idType"
                rules={[{ required: true, message: t('kycTips12', { ns: 'common' }) }]}
              >
                <MSelect placeholder={t('kycTips12', { ns: 'common' })}>
                  <Select.Option key="ID_CARD" value="ID_CARD">
                    {t('kycTips19', { ns: 'common' })}
                  </Select.Option>
                  <Select.Option key="PASSPORT" value="PASSPORT">
                    {t('kycTips20', { ns: 'common' })}
                  </Select.Option>
                </MSelect>
              </Form.Item>
              <Form.Item
                label={t('kycTips21', { ns: 'common' })}
                name="idNumber"
                rules={[{ required: true, message: t('kycTips11', { ns: 'common' }) }]}
              >
                <MInput placeholder={t('kycTips11', { ns: 'common' })} />
              </Form.Item>
              <Form.Item
                label={t('kycTips22', { ns: 'common' })}
                name="validType"
                rules={[{ required: true, message: t('kycTips11', { ns: 'common' }) }]}
              >
                <Radio.Group>
                  <Radio value="LIMITED"> {t('kycTips23', { ns: 'common' })} </Radio>
                  <Radio value="LONG_TERM"> {t('kycTips24', { ns: 'common' })} </Radio>
                </Radio.Group>
              </Form.Item>
              {validType === 'LIMITED' && (
                <Form.Item
                  label={t('kycTips25', { ns: 'common' })}
                  name="expireDate"
                  rules={[{ required: true, message: t('kycTips12', { ns: 'common' }) }]}
                >
                  <MDatePicker placeholder={t('kycTips12', { ns: 'common' })} />
                </Form.Item>
              )}

              <Form.Item label={t('kycTips26', { ns: 'common' })}>
                <Upload {...props} showUploadList={false} disabled={uploadList.length >= 5}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
              {uploadList.map((x, index) => {
                return (
                  <div className={styles.imageItem}>
                    <div className={styles.image}>
                      <MImage url={uploadList[index]} width={60} height={60} contentMode="ScaleToFill" />
                    </div>
                    <div>
                      <DeleteOutlined onClick={() => onRemove(index)} className={styles.removeIcon} />
                    </div>
                  </div>
                );
              })}

              <div className={styles.footer}>
                <MButton type="primary" className={styles.btn} size="large" onClick={onSubmit}>
                  {t('confirm', { ns: 'common' })}
                </MButton>
              </div>
            </MForm>
          </>
        )}

        {currentStep === 1 && (
          <div className={styles.processWrap}>
            <ProcessIcon width={52} height={52} />
            <div className={styles.processText}>{t('kycTips27', { ns: 'common' })}</div>
          </div>
        )}
      </div>
    </MModal>
  );
};

export default CMP;
